import React from 'react';
import { colors } from '../../theme/tokens/colors';
import { PlayIcon, PauseIcon } from '../icons';
import './PlayButton.css';

export interface PlayButtonProps {
    /** Size of the button in pixels (default: 48) */
    size?: number;
    /** Whether audio is currently playing */
    isPlaying: boolean;
    /** Whether audio is loading */
    isLoading?: boolean;
    /** Progress value from 0 to 100 */
    progress: number;
    /** Click handler to toggle play/pause */
    onClick: (e: React.MouseEvent | React.TouchEvent) => void;
    /** Optional className for additional styling */
    className?: string;
}

/**
 * PlayButton - A circular play/pause button with animated progress ring
 * Designed for SoundCloud-style audio player UI
 */
export const PlayButton: React.FC<PlayButtonProps> = ({
    size = 48,
    isPlaying,
    isLoading = false,
    progress,
    onClick,
    className = '',
}) => {
    // Calculate SVG dimensions
    const strokeWidth = 3;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    // Ring color based on playing/loading state
    const ringColor = isLoading ? colors.accentYellow : (isPlaying ? colors.accentYellow : colors.lightGrey);

    return (
        <button
            className={`play-button ${isLoading ? 'play-button--loading' : ''} ${className}`}
            onClick={onClick}
            style={{ width: size, height: size }}
            aria-label={isLoading ? 'Loading' : (isPlaying ? 'Pause' : 'Play')}
            type="button"
        >
            {/* Progress Ring SVG */}
            <svg
                className={`play-button__ring-container ${isLoading ? 'play-button__ring-container--spinning' : ''}`}
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
            >
                {/* Background ring (track) */}
                <circle
                    className="play-button__ring-bg"
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    strokeWidth={strokeWidth}
                />
                {/* Progress ring */}
                <circle
                    className="play-button__ring-progress"
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    strokeWidth={strokeWidth}
                    stroke={ringColor}
                    strokeDasharray={circumference}
                    strokeDashoffset={isLoading ? circumference * 0.75 : strokeDashoffset}
                    strokeLinecap="round"
                />
            </svg>

            {/* Icon - rendered directly without wrapper */}
            <div className="play-button__icon">
                {isPlaying ? (
                    <PauseIcon size="l" tone="inherit" style={{ width: 26, height: 26 }} />
                ) : (
                    <PlayIcon size="l" tone="inherit" style={{ width: 26, height: 26 }} />
                )}
            </div>
        </button>
    );
};

export default PlayButton;
